import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './OrderSuccess.css';

const OrderSuccess = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const orderId = location.state?.orderId;
    const orderNumber = location.state?.orderNumber;
    const tillNumber = location.state?.tillNumber;
    const totalAmount = location.state?.totalAmount;
    const customerName = location.state?.customerName;
    const customerPhone = location.state?.customerPhone;
    const orderItems = location.state?.orderItems;
    const paymentStatus = location.state?.paymentStatus;
    const paymentReference = location.state?.paymentReference;

    const CONTACT_NUMBER = '0113899836';
    const PAYBILL_NUMBER = location.state?.paybillNumber || tillNumber || '542542';
    const ACCOUNT_NUMBER = location.state?.accountNumber || '07609030756150';
    const BANK_NAME = 'I&M Bank';
    const DELIVERY_TIME = '2-3 business days';
    const isPaid = paymentStatus === 'paid';

    const normalizedWhatsApp = (() => {
        const digits = String(CONTACT_NUMBER).replace(/\D/g, '');
        if (digits.startsWith('0')) return `254${digits.slice(1)}`;
        if (digits.startsWith('254')) return digits;
        return digits;
    })();

    const orderNo = orderNumber || (orderId ? orderId.substring(0, 8).toUpperCase() : '');

    const receiptText = (() => {
        const lines = [];
        lines.push('Think Twice Second Hand Clothes - Order Receipt');
        lines.push(`Order Number: ${orderNo}`);
        if (customerName) lines.push(`Customer: ${customerName}`);
        if (customerPhone) lines.push(`Phone: ${customerPhone}`);
        if (typeof totalAmount === 'number') lines.push(`Total: KSh ${totalAmount.toLocaleString()}`);
        lines.push('');

        if (Array.isArray(orderItems) && orderItems.length > 0) {
            lines.push('Items:');
            orderItems.forEach((it, idx) => {
                const name = it?.productName || it?.title || 'Item';
                const qty = it?.productQuantity ?? 1;
                const price = it?.productPrice;
                const variant = it?.variantName ? ` (${it.variantName})` : '';
                const lineTotal = typeof price === 'number' ? (price * qty) : null;
                lines.push(`${idx + 1}. ${name}${variant} x${qty}${lineTotal !== null ? ` = KSh ${Number(lineTotal).toLocaleString()}` : ''}`);
            });
            lines.push('');
        }

        if (isPaid) {
            lines.push(`Payment: M-Pesa STK Push${paymentReference ? ` (${paymentReference})` : ''}`);
        } else {
            lines.push(`Payment: Paybill ${PAYBILL_NUMBER} (Account ${ACCOUNT_NUMBER}) - ${BANK_NAME}`);
        }
        lines.push(`Delivery: ${DELIVERY_TIME}`);
        lines.push('');
        lines.push(`Contact: ${CONTACT_NUMBER}`);
        return lines.join('\n');
    })();

    const whatsappHref = `https://wa.me/${normalizedWhatsApp}?text=${encodeURIComponent(receiptText)}`;

    if (!orderId) {
        navigate('/');
        return null;
    }

    return (
        <>
            <Helmet>
                <title>Order Successful | Think Twice Second Hand Clothes</title>
            </Helmet>
            <section id="order-success">
                <div className="container">
                    <div className="success-card">
                        <div className="success-icon">
                            <i className="bi bi-check-circle"></i>
                        </div>
                        <h1>Order Placed Successfully!</h1>
                        <p className="success-subtitle">
                            {isPaid
                                ? 'Your payment has been confirmed. We’ll process your delivery shortly.'
                                : 'Your order has been received. Please complete payment to process your delivery.'}
                        </p>

                        {isPaid ? (
                            <div className="payment-instructions-box">
                                <h3>✅ Payment Confirmed</h3>
                                <p>Thank you! We’ve received your M-Pesa payment.</p>
                                {paymentReference && (
                                    <p><strong>M-Pesa Ref:</strong> {paymentReference}</p>
                                )}
                            </div>
                        ) : (
                            <div className="payment-instructions-box">
                                <h3>📱 Complete Your Payment</h3>
                                <div className="payment-steps-success">
                                    <p><strong>1.</strong> Go to M-Pesa on your phone</p>
                                    <p><strong>2.</strong> Select "Lipa na M-Pesa" → "Pay Bill"</p>
                                    <p><strong>3.</strong> Enter Paybill Number: <span className="till-number-highlight">{PAYBILL_NUMBER}</span> ({BANK_NAME})</p>
                                    <p><strong>4.</strong> Enter Account Number: <span className="amount-highlight">{ACCOUNT_NUMBER}</span></p>
                                    <p><strong>5.</strong> Enter Amount: <span className="amount-highlight">KSh {totalAmount?.toLocaleString()}</span></p>
                                    <p><strong>6.</strong> Enter your M-Pesa PIN and confirm</p>
                                    <p><strong>7.</strong> Call us on <a href={`tel:${CONTACT_NUMBER}`} className="contact-link">{CONTACT_NUMBER}</a> to confirm</p>
                                </div>
                                <div className="important-note">
                                    <p>⚠️ <strong>Important:</strong> Please call us after making payment with your M-Pesa reference number to confirm your order.</p>
                                </div>
                            </div>
                        )}

                        <div className="order-info">
                            <p><strong>Order Number:</strong> {orderNo}</p>
                            {typeof totalAmount === 'number' && (
                                <p><strong>Total Paid:</strong> KSh {totalAmount.toLocaleString()}</p>
                            )}
                            <p><strong>Payment Method:</strong> {isPaid ? 'M-Pesa STK Push' : `Paybill ${PAYBILL_NUMBER} (Account ${ACCOUNT_NUMBER})`}</p>
                            <p><strong>Delivery Time:</strong> {DELIVERY_TIME}</p>
                        </div>

                        <div className="next-steps">
                            <h3>What's Next?</h3>
                            <ul>
                                <li>We'll call you on {customerPhone} to confirm your delivery details</li>
                                <li>Your order will be delivered within {DELIVERY_TIME}</li>
                                <li>Keep your order number ({orderNo}) for tracking</li>
                                <li>For any questions, contact us at {CONTACT_NUMBER}</li>
                            </ul>
                        </div>

                        <div className="whatsapp-receipt">
                            <h3>Send Receipt on WhatsApp</h3>
                            <p>Tap below to send your order receipt via WhatsApp.</p>
                            <a
                                className="btn-whatsapp"
                                href={whatsappHref}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <i className="bi bi-whatsapp me-2"></i>
                                Send Receipt ({CONTACT_NUMBER})
                            </a>
                        </div>

                        <div className="action-buttons">
                            <a href="/home" className="btn-primary">Continue Shopping</a>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default OrderSuccess;
